"use client"

import { useState, useEffect } from "react"
import { postAws } from "../postAws"
import { getCourseDetials } from "./api"

export const AIFeatures = ({ courseId, course, updateCourse, showToast, fetchCourseDetails, currentTopic }) => {
  const [generatedOutline, setGeneratedOutline] = useState(null)
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false)
  const [showOutlineConfirmation, setShowOutlineConfirmation] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // AI quiz generation
  const [isAiQuizMode, setIsAiQuizMode] = useState(false)
  const [aiQuizData, setAiQuizData] = useState({
    title: "",
    description: "",
    questionCount: 5,
    difficulty: "medium", // "easy", "medium", "hard"
  })
  const [subtopicsForQuiz, setSubtopicsForQuiz] = useState([])
  const [selectedSubtopics, setSelectedSubtopics] = useState([])
  const [generatingQuiz, setGeneratingQuiz] = useState(false)
  const [generatedQuizId, setGeneratedQuizId] = useState(null)
  const [quizPollingInterval, setQuizPollingInterval] = useState(null)

  // Clean up polling interval on unmount
  useEffect(() => {
    return () => {
      if (quizPollingInterval) {
        clearInterval(quizPollingInterval)
      }
    }
  }, [quizPollingInterval])

  const generateCourseOutline = async () => {
    if (!course || !course.name) {
      showToast("Course title is required to generate an outline", "error")
      return
    }

    setIsGeneratingOutline(true)
    try {
      const payload = {
        request: "create-outline-course",
        body: {
          title: course.name,
          description: course.description || "",
        },
      }

      const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)

      if (response.statusCode === 200) {
        const responseData = JSON.parse(response.body)
        // Parse the outline string since it's now a stringified JSON
        const outlineData = JSON.parse(responseData.outline)
        setGeneratedOutline(outlineData)
        setShowOutlineConfirmation(true)
      } else {
        throw new Error(JSON.parse(response.body).message || "Failed to generate course outline")
      }
    } catch (error) {
      console.error("Error generating course outline:", error)
      showToast("Error generating course outline. Please try again.", "error")
    } finally {
      setIsGeneratingOutline(false)
    }
  }

  const addGeneratedOutlineToCourse = async () => {
    if (!generatedOutline || !generatedOutline.topics) return

    setIsSaving(true)
    try {
      // First, add all topics
      for (const topic of generatedOutline.topics) {
        const topicResponse = await postAws("https://api-dev.aurait.ai/eLearning", "POST", {
          request: "add-topic",
          body: {
            courseId: courseId,
            topicTitle: topic.title,
          },
        })

        if (topicResponse.statusCode !== 200) {
          throw new Error("Failed to add topic: " + topic.title)
        }

        const topicData = JSON.parse(topicResponse.body)
        const topicId = topicData.topic.id

        // Then add all subtopics for this topic
        if (topic.subtopics && topic.subtopics.length > 0) {
          for (const subtopic of topic.subtopics) {
            const subtopicPayload = {
              request: "add-subtopic",
              body: {
                courseId: courseId,
                topicId: topicId,
                subtopicTitle: subtopic.title,
                subtopicDescription: subtopic.description || "",
                contentType: subtopic.contentType || "video",
              },
            }

            // Add specific fields based on content type
            if (subtopic.contentType === "quiz") {
              // Create a default quiz structure
              const defaultQuiz = [
                {
                  id: Date.now(),
                  text: "Sample question",
                  options: [
                    { id: Date.now() + 1, text: "Option 1", isCorrect: true },
                    { id: Date.now() + 2, text: "Option 2", isCorrect: false },
                    { id: Date.now() + 3, text: "Option 3", isCorrect: false },
                    { id: Date.now() + 4, text: "Option 4", isCorrect: false },
                  ],
                },
              ]
              subtopicPayload.body.quizData = JSON.stringify(defaultQuiz)
              subtopicPayload.body.totalMarks = 1
            }

            await postAws("https://api-dev.aurait.ai/eLearning", "POST", subtopicPayload)
          }
        }
      }

      // Refresh course details to show the new structure
      await fetchCourseDetails()
      showToast("Course outline added successfully", "success")
    } catch (error) {
      console.error("Error adding course outline:", error)
      showToast("Error adding course outline. Please try again.", "error")
    } finally {
      setIsSaving(false)
      setShowOutlineConfirmation(false)
      setGeneratedOutline(null)
    }
  }

  const fetchSubtopicsForQuiz = (topicId) => {
    // Safety check - if course is not loaded yet
    if (!course || !course.topics) {
      console.log("Course or topics not available yet", course)
      setSubtopicsForQuiz([])
      return []
    }

    if (!topicId) {
      console.error("No topic ID provided to fetchSubtopicsForQuiz")
      showToast("No topic selected", "error")
      setSubtopicsForQuiz([])
      return []
    }

    console.log(
      "All available topics:",
      course.topics.map((t) => ({ id: t.id, title: t.title })),
    )

    // Find the selected topic
    const selectedTopic = course.topics.find((topic) => topic.id === topicId)

    if (!selectedTopic) {
      console.log(
        "Topic not found for ID:",
        topicId,
        "Available topic IDs:",
        course.topics.map((t) => t.id),
      )
      showToast("Topic not found", "error")
      setSubtopicsForQuiz([])
      return []
    }

    console.log("Selected topic:", selectedTopic)

    // Check if we have content in the topic
    if (!selectedTopic.content && !selectedTopic.subtopics) {
      console.log("No content or subtopics found in topic")
      setSubtopicsForQuiz([])
      return []
    }

    // Try to get content from either content or subtopics property
    const topicContent = selectedTopic.subtopics || selectedTopic.content || []
    console.log("Topic content:", topicContent)

    // Get all subtopics that are not quizzes (videos, pdfs, etc.)
    const availableSubtopics = topicContent
      .filter((item) => {
        const itemType = item.type || item.contentType || ""
        return itemType !== "quiz"
      })
      .map((item) => ({
        id: item.id,
        title: item.title,
        description: item.description || "",
        type: item.type || item.contentType || "unknown",
        selected: false,
      }))

    console.log("Available subtopics:", availableSubtopics)
    setSubtopicsForQuiz(availableSubtopics)
    setSelectedSubtopics([]) // Reset selected subtopics
    return availableSubtopics
  }

  const toggleSubtopicSelection = (subtopicId) => {
    setSelectedSubtopics((prev) => {
      if (prev.includes(subtopicId)) {
        return prev.filter((id) => id !== subtopicId)
      } else {
        return [...prev, subtopicId]
      }
    })
  }

const handleAiQuizSubmit = async (e) => {
  e.preventDefault()

  // Validate quiz data
  if (!aiQuizData.title.trim()) {
    showToast("Please enter a quiz title", "error")
    return Promise.reject("Missing title")
  }

  if (selectedSubtopics.length === 0) {
    showToast("Please select at least one subtopic for the quiz", "error")
    return Promise.reject("No subtopics selected")
  }

  setGeneratingQuiz(true)

  try {
    const selectedSubtopicsData = []
    course.topics.forEach((topic) => {
      if (topic.content) {
        topic.content.forEach((subtopic) => {
          if (selectedSubtopics.includes(subtopic.id)) {
            selectedSubtopicsData.push({
              id: subtopic.id,
              title: subtopic.title,
              description: subtopic.description || "",
            })
          }
        })
      }
    })

    const payload = {
      request: "create-quiz",
      body: {
        courseId: courseId,
        topicId: currentTopic,
        quizTitle: aiQuizData.title,
        quizDescription: aiQuizData.description,
        questionCount: Number.parseInt(aiQuizData.questionCount),
        difficulty: aiQuizData.difficulty,
        selectedSubtopics: selectedSubtopicsData,
      },
    }

    showToast("Quiz generation has started. This may take a minute or two.", "info")

    const response = await postAws("https://api-dev.aurait.ai/eLearning", "POST", payload)

    if (response.statusCode === 200) {
      const responseData = JSON.parse(response.body)
      const quizId = responseData.quiz.id

      setGeneratedQuizId(quizId)

      const intervalId = setInterval(() => checkQuizStatus(quizId, currentTopic), 5000)
      setQuizPollingInterval(intervalId)

      updateCourse((prev) => ({
        ...prev,
        topics: prev.topics.map((topic) => {
          if (topic.id === currentTopic) {
            const newQuizContent = {
              id: quizId,
              title: aiQuizData.title,
              description: aiQuizData.description,
              type: "quiz",
              isGenerating: true,
              generationStatus: "generating",
              quizStatus: "generating",
              questionCount: Number.parseInt(aiQuizData.questionCount),
              difficulty: aiQuizData.difficulty,
              selectedSubtopicCount: selectedSubtopics.length,
            }

            return {
              ...topic,
              content: [...(topic.content || []), newQuizContent],
            }
          }
          return topic
        }),
      }))

      setSelectedSubtopics([])
      setIsAiQuizMode(false)

      // ✅ Show toast, wait 2 seconds, then reload
      showToast("Quiz generation started successfully!", "success")
      setTimeout(() => {
        window.location.reload()
      }, 2000)

      return Promise.resolve()
    } else {
      throw new Error(JSON.parse(response.body).message || "Failed to start quiz generation")
    }
  } catch (error) {
    console.error("Error generating quiz:", error)
    showToast("Error starting quiz generation. Please try again.", "error")
    return Promise.reject(error)
  } finally {
    setGeneratingQuiz(false)
  }
}


  const checkQuizStatus = async (quizId, topicId) => {
    try {
      // Make API call to check quiz status
      const response = await getCourseDetials(courseId)
      const courseData = JSON.parse(response)

      if (!courseData || !courseData.course) {
        throw new Error("Invalid course data structure")
      }

      const course = courseData.course

      // Find the topic and quiz within the response
      const topic = course.topics.find((t) => t.id === topicId)
      if (!topic) return

      const quiz = topic.subtopics?.find((st) => st.id === quizId) || topic.content?.find((st) => st.id === quizId)
      if (!quiz) return

      // Check if the quiz is now created
      if (quiz.quizStatus === "created" || quiz.type === "quiz") {
        // Quiz is ready, clear the polling interval
        clearInterval(quizPollingInterval)
        setQuizPollingInterval(null)

        // Show success message
        showToast("Quiz has been successfully generated!", "success")

        // Refresh the course details to show the new quiz
        await fetchCourseDetails()

        // Clear the generated quiz ID
        setGeneratedQuizId(null)
      }
    } catch (error) {
      console.error("Error checking quiz status:", error)
      // If there's an error, we'll continue polling
    }
  }

  return {
    generateCourseOutline,
    addGeneratedOutlineToCourse,
    fetchSubtopicsForQuiz,
    toggleSubtopicSelection,
    handleAiQuizSubmit,
    generatedOutline,
    isGeneratingOutline,
    showOutlineConfirmation,
    setShowOutlineConfirmation,
    isSaving,
    isAiQuizMode,
    setIsAiQuizMode,
    aiQuizData,
    setAiQuizData,
    subtopicsForQuiz,
    generatingQuiz,
    selectedSubtopics,
  }
}
